import React, { Suspense, lazy, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Link, useLocation } from 'react-router';

import { SidebarTrigger } from './ui/sidebar';

const ArrowLeft = lazy(() => import('lucide-react').then(mod => ({ default: mod.ArrowLeft })));
const GraduationCap = lazy(() =>
  import('lucide-react').then(mod => ({ default: mod.GraduationCap })),
);
const User = lazy(() => import('lucide-react').then(mod => ({ default: mod.User })));
const CreditCard = lazy(() => import('lucide-react').then(mod => ({ default: mod.CreditCard })));

const UUID_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

const ROOT_PATHNAMES = [
  {
    pathname: '/course',
    name: '강좌관리',
    icon: (
      <Suspense fallback={<div className="w-5 h-5" />}>
        <GraduationCap aria-hidden="true" />
      </Suspense>
    ),
    description: '',
  },
  {
    pathname: '/my',
    name: '내 정보관리',
    icon: (
      <Suspense fallback={<div className="w-5 h-5" />}>
        <User aria-hidden="true" />
      </Suspense>
    ),
    description: '',
  },
  {
    pathname: '/payment',
    name: '결제/환불 내역',
    icon: (
      <Suspense fallback={<div className="w-5 h-5" />}>
        <CreditCard aria-hidden="true" />
      </Suspense>
    ),
    description: '',
  },
];

const Header = () => {
  const location = useLocation();
  const pathname = location.pathname;

  const [backLink, setBackLink] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState<{ href?: string; name: string }[]>([]);

  useEffect(() => {
    if (!pathname) return;

    if (new RegExp(`^/course/${UUID_PATTERN}$`, 'i').test(pathname)) {
      setBackLink('/course');
      setBreadcrumbs([{ href: '/course', name: '강좌관리' }, { name: '과제' }]);
    }

    if (new RegExp(`^/course/${UUID_PATTERN}/notice$`, 'i').test(pathname)) {
      setBackLink('/course');
      setBreadcrumbs([{ href: '/course', name: '강좌관리' }, { name: '공지사항' }]);
    }

    if (new RegExp(`^/course/${UUID_PATTERN}/${UUID_PATTERN}/solve$`, 'i').test(pathname)) {
      const match = pathname.match(
        new RegExp(`^/course/(${UUID_PATTERN})/${UUID_PATTERN}/solve$`, 'i'),
      );
      if (match) {
        setBackLink('/course');
        setBreadcrumbs([
          { href: '/course', name: '강좌관리' },
          { href: `/course/${match[1]}`, name: '과제' },
          { name: '과제 풀이' },
        ]);
      }
    }
    if (new RegExp(`^/course/${UUID_PATTERN}/${UUID_PATTERN}$`, 'i').test(pathname)) {
      const match = pathname.match(new RegExp(`^/course/(${UUID_PATTERN})/${UUID_PATTERN}$`, 'i'));
      if (match) {
        setBackLink(`/course/${match[1]}`);
        setBreadcrumbs([
          { href: '/course', name: '강좌관리' },
          { href: `/course/${match[1]}`, name: '과제' },
          { name: '과제 상세' },
        ]);
      }
    }
  }, [pathname]);

  const rootPath = ROOT_PATHNAMES.find(path => path.pathname === pathname);

  if (rootPath) {
    return (
      <div className="p-8 bg-white border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SidebarTrigger className="-ml-1 block lg:hidden" />

            <div className="w-10 h-10 bg-primary-blue rounded-lg flex items-center justify-center text-white">
              {rootPath.icon}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{rootPath.name}</h1>
              <p className="text-muted-foreground">{rootPath.name}</p>
            </div>
          </div>
          <div id="header_buttons"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-b border-gray-200">
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-4">
            <SidebarTrigger className="-ml-1 block lg:hidden" />

            <Link to={backLink} className="text-black">
              <Suspense fallback={<div className="h-5 w-5" />}>
                <ArrowLeft className="h-5 w-5" />
              </Suspense>
            </Link>
            <nav className="flex items-center space-x-2 text-sm text-black">
              {breadcrumbs.map((breadcrumb, index) => {
                const { href, name } = breadcrumb;
                return (
                  <React.Fragment key={index}>
                    {index !== 0 && <span>/</span>}
                    {href && <Link to={href}>{name}</Link>}
                    {!href && <div>{name}</div>}
                  </React.Fragment>
                );
              })}
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;

export function HeaderButtonsPortal({ children }: { children: React.ReactNode }) {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const el = document.getElementById('header_buttons');
    setContainer(el);
  }, []);

  if (!container) return null;

  return createPortal(children, container);
}
